import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText,
  Users,
  Crown,
  ArrowRight,
  Check,
  Calendar,
  MessageCircle,
  Bell,
  Zap,
  HardDrive,
  Volume2,
  X,
  Sparkles,
  BookOpen,
  Tag,
  Share2,
  Coffee,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import PlusModal from './PlusModal';

// ─── Adım verileri ───────────────────────────────────────────────────────────

const getSteps = (t) => [
  {
    id: 1,
    tag: '1/4',
    accentColor: '#3d8ef0',
    accentBg: 'rgba(61,142,240,0.12)',
    visual: <WelcomeVisual />,
    title: t('onboardingModal.steps.1.title'),
    subtitle: t('onboardingModal.steps.1.subtitle'),
    primaryLabel: t('onboardingModal.steps.1.primaryLabel'),
    features: null,
  },
  {
    id: 2,
    tag: '2/4',
    accentColor: '#22c55e',
    accentBg: 'rgba(34,197,94,0.10)',
    visual: <NotesVisual />,
    title: t('onboardingModal.steps.2.title'),
    subtitle: t('onboardingModal.steps.2.subtitle'),
    primaryLabel: t('onboardingModal.steps.2.primaryLabel'),
    features: [
      { icon: FileText,  text: t('onboardingModal.steps.2.features.richEditor') },
      { icon: Tag,       text: t('onboardingModal.steps.2.features.categories') },
      { icon: BookOpen,  text: t('onboardingModal.steps.2.features.sync') },
    ],
  },
  {
    id: 3,
    tag: '3/4',
    accentColor: '#a78bfa',
    accentBg: 'rgba(167,139,250,0.10)',
    visual: <CollabVisual />,
    title: t('onboardingModal.steps.3.title'),
    subtitle: t('onboardingModal.steps.3.subtitle'),
    primaryLabel: t('onboardingModal.steps.3.primaryLabel'),
    features: [
      { icon: Share2,        text: t('onboardingModal.steps.3.features.collabEdit') },
      { icon: MessageCircle, text: t('onboardingModal.steps.3.features.chat') },
      { icon: Calendar,      text: t('onboardingModal.steps.3.features.calendar') },
    ],
  },
  {
    id: 4,
    tag: '4/4',
    accentColor: '#f59e0b',
    accentBg: 'rgba(245,158,11,0.10)',
    visual: <TrialVisual />,
    title: t('onboardingModal.steps.4.title'),
    subtitle: t('onboardingModal.steps.4.subtitle'),
    primaryLabel: t('onboardingModal.steps.4.primaryLabel'),
    secondaryLabel: t('onboardingModal.steps.4.secondaryLabel'),
    features: [
      { icon: HardDrive, text: t('onboardingModal.steps.4.features.upload') },
      { icon: Users,     text: t('onboardingModal.steps.4.features.collabEdit') },
      { icon: Volume2,   text: t('onboardingModal.steps.4.features.sounds') },
      { icon: Zap,       text: t('onboardingModal.steps.4.features.support') },
    ],
  },
];

// ─── Görsel bileşenler ────────────────────────────────────────────────────────

function WelcomeVisual() {
  const { t } = useTranslation('shared');
  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center gap-5 select-none">
      {/* Arka plan daireler */}
      <div className="absolute w-64 h-64 rounded-full bg-[#3d8ef0]/10 blur-3xl pointer-events-none" />
      <div className="absolute w-40 h-40 rounded-full bg-[#3d8ef0]/15 blur-2xl pointer-events-none translate-x-8 translate-y-6" />

      {/* Ana logo */}
      <motion.div
        className="relative z-10 w-24 h-24 rounded-3xl bg-[#1e2d47] border border-[#3d8ef0]/30 flex items-center justify-center shadow-[0_0_40px_rgba(61,142,240,0.25)]"
        animate={{ scale: [1, 1.04, 1] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      >
        <FileText className="w-12 h-12 text-[#3d8ef0]" strokeWidth={1.5} />
      </motion.div>

      {/* Yüzen mini kartlar */}
      {[
        { icon: Bell,          label: t('onboardingModal.welcomeVisual.reminders'), x: -110, y: -60, delay: 0 },
        { icon: MessageCircle, label: t('onboardingModal.welcomeVisual.chat'),      x: 110,  y: -40, delay: 0.4 },
        { icon: Calendar,      label: t('onboardingModal.welcomeVisual.calendar'),  x: -100, y: 70,  delay: 0.8 },
        { icon: Coffee,        label: t('onboardingModal.welcomeVisual.break'),     x: 105,  y: 70,  delay: 1.2 },
      ].map(({ icon: Icon, label, x, y, delay }) => (
        <motion.div
          key={label}
          className="absolute z-10 flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-[#161f31] border border-white/[0.08] shadow-lg"
          style={{ transform: `translate(${x}px, ${y}px)` }}
          animate={{ y: [y, y - 6, y] }}
          transition={{ duration: 2.5 + delay * 0.3, repeat: Infinity, ease: 'easeInOut', delay }}
        >
          <Icon className="w-3.5 h-3.5 text-[#3d8ef0]" />
          <span className="text-[10px] text-[#c8d8eb] font-medium whitespace-nowrap">{label}</span>
        </motion.div>
      ))}
    </div>
  );
}

function NotesVisual() {
  const { t } = useTranslation('shared');
  const notes = [
    { title: t('onboardingModal.notesVisual.note1.title'), preview: t('onboardingModal.notesVisual.note1.preview'), color: '#3d8ef0', tag: t('onboardingModal.notesVisual.note1.tag') },
    { title: t('onboardingModal.notesVisual.note2.title'), preview: t('onboardingModal.notesVisual.note2.preview'), color: '#22c55e', tag: t('onboardingModal.notesVisual.note2.tag') },
    { title: t('onboardingModal.notesVisual.note3.title'), preview: t('onboardingModal.notesVisual.note3.preview'), color: '#a78bfa', tag: t('onboardingModal.notesVisual.note3.tag') },
  ];
  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center gap-3 px-6 select-none">
      <div className="absolute w-56 h-56 rounded-full bg-[#22c55e]/10 blur-3xl pointer-events-none" />
      {notes.map((note, i) => (
        <motion.div
          key={note.title}
          className="relative z-10 w-full max-w-[260px] rounded-xl bg-[#161f31] border border-white/[0.07] p-3 shadow-lg"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.15 }}
        >
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: note.color }} />
            <span className="text-xs font-semibold text-[#dde8f5] flex-1 truncate">{note.title}</span>
            <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium"
              style={{ background: note.color + '22', color: note.color }}>
              {note.tag}
            </span>
          </div>
          <p className="text-[10px] text-[#4d6a8a] truncate pl-4">{note.preview}</p>
        </motion.div>
      ))}
    </div>
  );
}

function CollabVisual() {
  const { t } = useTranslation('shared');
  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center gap-4 select-none">
      <div className="absolute w-56 h-56 rounded-full bg-[#a78bfa]/10 blur-3xl pointer-events-none" />

      {/* Avatar grubu */}
      <div className="relative z-10 flex -space-x-3 mb-2">
        {['#3d8ef0', '#22c55e', '#f59e0b', '#a78bfa'].map((color, i) => (
          <motion.div
            key={color}
            className="w-11 h-11 rounded-full border-2 border-[#0d1424] flex items-center justify-center text-sm font-bold"
            style={{ backgroundColor: color + '33', color, borderColor: '#0d1424' }}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: i * 0.12 }}
          >
            {['A', 'B', 'C', 'D'][i]}
          </motion.div>
        ))}
        <motion.div
          className="w-11 h-11 rounded-full border-2 border-[#0d1424] bg-[#1e2d47] flex items-center justify-center text-[10px] text-[#4d6a8a] font-medium"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.5 }}
        >
          +12
        </motion.div>
      </div>

      {/* Chat baloncukları */}
      <div className="relative z-10 w-full px-5 flex flex-col gap-2.5">
        {[
          { text: t('onboardingModal.collabVisual.bubble1'), side: 'left',  delay: 0.1 },
          { text: t('onboardingModal.collabVisual.bubble2'), side: 'right', delay: 0.3 },
          { text: t('onboardingModal.collabVisual.bubble3'), side: 'left',  delay: 0.5 },
        ].map(({ text, side, delay }) => (
          <motion.div
            key={text}
            className={`max-w-[180px] px-3 py-2 rounded-2xl text-[11px] font-medium shadow ${
              side === 'right'
                ? 'self-end bg-[#1d3557] border border-[#3d8ef0]/25 text-[#c8d8eb]'
                : 'self-start bg-[#161f31] border border-white/[0.08] text-[#c8d8eb]'
            }`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay }}
          >
            {text}
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function TrialVisual() {
  const { t } = useTranslation('shared');
  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center gap-4 select-none">
      <div className="absolute w-64 h-64 rounded-full bg-[#f59e0b]/10 blur-3xl pointer-events-none" />

      {/* Crown */}
      <motion.div
        className="relative z-10 w-28 h-28 rounded-3xl flex items-center justify-center shadow-[0_0_60px_rgba(245,158,11,0.3)]"
        style={{ background: 'radial-gradient(circle at 50% 40%, #1e2d47, #0d1424)' }}
        animate={{ scale: [1, 1.06, 1] }}
        transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
      >
        <Crown className="w-14 h-14 text-amber-400" strokeWidth={1.5} />
        {/* Parıltılar */}
        {[
          { top: '-12px', right: '10px', size: 16, delay: 0 },
          { top: '10px',  right: '-14px', size: 12, delay: 0.4 },
          { top: '-8px',  left: '12px',   size: 10, delay: 0.8 },
        ].map((s, i) => (
          <motion.div
            key={i}
            className="absolute"
            style={{ top: s.top, right: s.right, left: s.left }}
            animate={{ opacity: [0, 1, 0], scale: [0.5, 1.2, 0.5] }}
            transition={{ duration: 1.8, repeat: Infinity, delay: s.delay }}
          >
            <Sparkles style={{ width: s.size, height: s.size, color: '#fbbf24' }} />
          </motion.div>
        ))}
      </motion.div>

      {/* 3 gün rozeti */}
      <motion.div
        className="relative z-10 px-4 py-2 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-300 text-sm font-bold"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3 }}
      >
        {t('onboardingModal.trialVisual.badge')}
      </motion.div>
    </div>
  );
}

// ─── Ana modal ────────────────────────────────────────────────────────────────

const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? 40 : -40, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? -40 : 40, opacity: 0 }),
};

const OnboardingModal = ({ isOpen, onComplete }) => {
  const { t } = useTranslation('shared');
  const [stepIndex, setStepIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [showPlusModal, setShowPlusModal] = useState(false);

  const STEPS = getSteps(t);
  const step = STEPS[stepIndex];
  const isLast = stepIndex === STEPS.length - 1;

  const goNext = useCallback(() => {
    if (isLast) {
      onComplete?.();
      return;
    }
    setDirection(1);
    setStepIndex((i) => i + 1);
  }, [isLast, onComplete]);

  const handleSkip = useCallback(() => {
    onComplete?.();
  }, [onComplete]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 z-[99000] flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            {/* Overlay */}
            <div className="absolute inset-0 bg-black/55 backdrop-blur-[8px]" />

            {/* Modal kart */}
            <motion.div
              className="relative z-10 flex overflow-hidden"
              style={{
                width: '90vw',
                maxWidth: 1100,
                height: '85vh',
                maxHeight: 760,
                background: '#0d1424',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 20,
                boxShadow: '0 40px 120px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.04)',
              }}
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            >
              {/* Sol panel — görsel */}
              <div
                className="relative hidden md:flex flex-col items-center justify-center flex-shrink-0 overflow-hidden"
                style={{
                  width: '42%',
                  background: `radial-gradient(ellipse at 50% 40%, ${step.accentBg}, transparent 70%), #080e1a`,
                  borderRight: '1px solid rgba(255,255,255,0.05)',
                }}
              >
                <AnimatePresence mode="wait" custom={direction}>
                  <motion.div
                    key={step.id}
                    custom={direction}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                    className="w-full h-full"
                  >
                    {step.visual}
                  </motion.div>
                </AnimatePresence>

                {/* Adım numarası sol altta */}
                <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-2">
                  {STEPS.map((_, i) => (
                    <div
                      key={i}
                      className="rounded-full transition-all duration-300"
                      style={{
                        width: i === stepIndex ? 20 : 6,
                        height: 6,
                        background: i === stepIndex ? step.accentColor : 'rgba(255,255,255,0.15)',
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Sağ panel — içerik */}
              <div className="flex flex-col flex-1 min-w-0 relative">
                {/* Atla butonu */}
                <div className="absolute top-5 right-5 z-10">
                  <button
                    onClick={handleSkip}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[#4d6a8a] hover:text-[#c8d8eb] hover:bg-white/[0.05] transition-colors text-sm"
                  >
                    <X className="w-3.5 h-3.5" />
                    {t('onboardingModal.skip')}
                  </button>
                </div>

                {/* İçerik alanı */}
                <div className="flex flex-col flex-1 justify-center px-8 md:px-10 pb-8 pt-16 overflow-y-auto">
                  <AnimatePresence mode="wait" custom={direction}>
                    <motion.div
                      key={step.id}
                      custom={direction}
                      variants={slideVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
                      className="flex flex-col gap-6"
                    >
                      {/* Adım etiketi */}
                      <span
                        className="text-xs font-semibold uppercase tracking-widest"
                        style={{ color: step.accentColor }}
                      >
                        {t('onboardingModal.stepLabel', { tag: step.tag })}
                      </span>

                      {/* Başlık */}
                      <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-white leading-tight mb-3">
                          {step.title}
                        </h1>
                        <p className="text-[#8aaccc] text-sm md:text-base leading-relaxed">
                          {step.subtitle}
                        </p>
                      </div>

                      {/* Özellik listesi */}
                      {step.features && (
                        <div className="space-y-3">
                          {step.features.map(({ icon: Icon, text }) => (
                            <div key={text} className="flex items-center gap-3">
                              <div
                                className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                                style={{ background: step.accentBg }}
                              >
                                <Icon className="w-4 h-4" style={{ color: step.accentColor }} />
                              </div>
                              <span className="text-sm text-[#c8d8eb]">{text}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Son adım — trial bilgi kartı */}
                      {isLast && (
                        <div className="rounded-xl bg-amber-500/10 border border-amber-500/20 p-4">
                          <div className="flex items-start gap-3">
                            <Check className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-amber-200/80 leading-relaxed">
                              {t('onboardingModal.trialInfo')}
                            </p>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  </AnimatePresence>
                </div>

                {/* Alt butonlar */}
                <div className="flex-shrink-0 px-8 md:px-10 pb-8 pt-2 flex flex-col gap-3">
                  {/* Mobilde progress dots */}
                  <div className="flex md:hidden justify-center gap-2 mb-2">
                    {STEPS.map((_, i) => (
                      <div
                        key={i}
                        className="rounded-full transition-all duration-300"
                        style={{
                          width: i === stepIndex ? 20 : 6,
                          height: 6,
                          background: i === stepIndex ? step.accentColor : 'rgba(255,255,255,0.15)',
                        }}
                      />
                    ))}
                  </div>

                  {/* İkincil buton (sadece son adımda) */}
                  {isLast && step.secondaryLabel && (
                    <button
                      onClick={() => setShowPlusModal(true)}
                      className="w-full py-3 rounded-xl border border-amber-500/30 text-amber-400 text-sm font-medium hover:bg-amber-500/10 transition-colors"
                    >
                      {step.secondaryLabel}
                    </button>
                  )}

                  {/* Ana buton */}
                  <button
                    onClick={goNext}
                    className="w-full py-3.5 rounded-xl text-white text-sm font-semibold flex items-center justify-center gap-2 transition-all hover:brightness-110 active:scale-[0.98]"
                    style={{ background: step.accentColor }}
                  >
                    {step.primaryLabel}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Plus detay modalı */}
      <PlusModal isOpen={showPlusModal} onClose={() => setShowPlusModal(false)} />
    </>,
    document.body
  );
};

export default OnboardingModal;
